<script setup>
import { showModal, hideModal } from '@/Utils/modalBus'
import { computed, ref, getCurrentInstance, onMounted, watch } from 'vue'
import { useStore } from 'vuex'
import { useNotification } from "@kyvg/vue3-notification"
import utils from '@/utils';
import StreamService from '@/services/StreamService';
import IconBase from '../../icon/IconBase.vue';
import IconAlertTriangle from '../../icon/IconAlertTriangle.vue';
import FormInput from '../../Atoms/FormInput.vue';
import GetRtmpSrtButtons from './GetRtmpSrtButtons.vue';

const props = defineProps({
  stream: {
    type: Object,
    required: true,
    validator(v) {
      return Object.prototype.hasOwnProperty.call(v, '_id');
    }
  },
  streamAlive: {
    type: Boolean
  },
  isRtmpPullEnabled: {
    type: Boolean,
    default: false
  },
  rmptPullUrlProcessing: {
    type: Boolean,
    default: false
  },
  transcoderView: {
    type: Boolean,
    default: false
  },
  streamSourceType: {
    type: String
  },
  sourceTypes: {
    type: Object
  },
  selectedLinkedSource: {
    type: Object,
    default: null
  },
  sourceSet: {
    type: Boolean,
    default: false
  },
  getStreamPushUrl: {
    type: String
  },
  getStreamUrlAndKey: {
    type: String,
  },
  streamKey: {
    type: String
  },
  isPasswordEnabled: {
    type: Boolean,
    default: false
  },
  isAnyBackupStreamLive: {
    type: Boolean,
    default: false
  },
  streamKeySrt: {
    type: String,
    default: null
  },
  srtRegionIngestDestination: {
    type: String,
    default: null
  },
})

const store = useStore()
const { notify } = useNotification()
const instance = getCurrentInstance();
const root = instance.proxy.$root;

const emit = defineEmits({
  'request-url': () => true,
  'set-linked-source': () => true,
  'init-selected-source': () => true,
  'selected-source': () => true,
})

const streamPullUrl = ref(null)
const streamPullError = ref(false)
const isPullSourceValid = ref(true)
const isLoading = ref(false)

const isCurrentTrial = computed(() => {
  const type = props.stream?.type
  if (type === 'live') {
    const meta = store.getters['User/subscriptionMeta']
    return meta?.live?.trial === true
  }
  if (type === 'restream') {
    const sub = store.getters['User/baseSubscription']
    if (sub?.package?.category !== 'restream') return false
    const isTrialByCharge = Number(sub?.package?.baseCharge ?? 0) === 0
    const isTrialByName = /trial/gi.test(sub?.package?.name || '')
    return isTrialByCharge || isTrialByName
  }
  return false
})

onMounted(async () => {
  streamPullUrl.value = props.stream?.pullUrl
})

// The tab stays mounted (v-show), so mirror external changes such as switching
// to Publish, which clears the pull URL on the stream.
watch(() => props.stream?.pullUrl, (pullUrl) => {
  streamPullUrl.value = pullUrl
  isPullSourceValid.value = true
  streamPullError.value = false
})

const pullSourceValidation = () => {
  // Clean the input string by removing all whitespace characters
  const pullSource = streamPullUrl.value?.replace(/\s/g, "");
  streamPullUrl.value = pullSource;
  if (pullSource?.length > 0) {
    isPullSourceValid.value = utils.validateSrtUrl(pullSource)
  } else {
    isPullSourceValid.value = true
  }
}

const onPullUrlChange = () => {
  pullSourceValidation();
  streamPullError.value = false;
}

const isDisabled = computed(() => {
  if (streamPullUrl.value?.length === 0) return true
  if (streamPullUrl.value === props.stream?.pullUrl) return true
  if (!isPullSourceValid.value) return true
  return false
})

const isMixerPullAuthorized = () => {
  const exlcudedRegions = ['br'];
  const curRegion = props.stream.region?.identifier;

  let bypassed = true;
  for (let i = 0; i < exlcudedRegions.length; i++) {
    if (curRegion === exlcudedRegions[i]) {
      bypassed = false;
      break;
    }
  }
  return bypassed;
}

const parseURL = (srcURL) => {
  let url;
  try {
    url = new URL(srcURL);
  } catch (_) {
    return false;
  }
  return url
}

const setStreamPullUrl = async () => {
  const pullSource = streamPullUrl.value.replace(/\s/g, '');
  if (utils.isMixerFTLSource(pullSource) && !isMixerPullAuthorized()) {
    showModal('alert-mixer-pull');
    return;
  }
  // Block HLS pull for trial users based on current stream type
  if (isCurrentTrial.value) {
    const url = parseURL(pullSource)
    if (url.protocol === "http:" || url.protocol === "https:") {
      showModal('modal-pull-hls-premium');
      return;
    }
  }

  try {
    isLoading.value = true
    await StreamService.setStreamPullUrl(props.stream._id, pullSource);
    isLoading.value = false
    await store.dispatch('Streams/patchStream', {
      streamId: props.stream._id,
      patch: { pullUrl: pullSource }
    })
    emit('stream-updated', { pullUrl: pullSource });
    
    notify({ group: 'success', text: 'Stream pull URL saved.' });
  } catch (e) {
    notify({
      group: 'error',
      text: 'Unable to save the stream pull URL.'
    });
  }
}

const requestUrl = (url) => emit('request-url', url);
</script>
<template>
  <!-- Publish source tabs -->
  <div class="flex flex-wrap w-full mt-3">
    <div class="w-full bg-surface-1 rounded-lg p-3">
      <div class="flex items-center justify-between mb-1">
        <label class="mb-0 text-sm text-surface-8">Pull Source</label>
        <code v-if="stream.enabled && stream.pullUrl" class="text-tiny"
          :class="streamAlive ? 'text-main' : 'text-warning'">{{ streamAlive ? 'Connected' : 'Connecting' }}</code>
      </div>
      <FormInput v-model="streamPullUrl" @blur="pullSourceValidation" @keypress="onPullUrlChange()"
        :isError="!isPullSourceValid" placeholder="Specify the source URL" />
      <p class="flex gap-x-2 text-sm mt-2 mb-0" :class="isPullSourceValid ? 'text-surface-7' : 'text-danger'">
        <icon-base classIcon="mt-1">
          <icon-alert-triangle />
        </icon-base>
        <span>Please make sure the Pull URL follows our <a class="underline" target="_blank"
            href="https://docs.castr.com/en/articles/5268042-supported-protocols-for-pull-mode">supported
            formats</a></span>
      </p>
    </div>

    <GetRtmpSrtButtons v-if="!transcoderView" @request-url="requestUrl" :stream="stream" :stream-alive="streamAlive"
      :is-rtmp-pull-enabled="isRtmpPullEnabled" :rmpt-pull-url-processing="rmptPullUrlProcessing"
      :stream-source-type="streamSourceType" :source-types="sourceTypes" :disabled="isDisabled" class="!justify-start mt-3"
      @set-stream-pull-url="setStreamPullUrl" :stream-source-type-processing="isLoading" />
  </div>
</template>